import { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '../utils/api';
import { useAuth } from './useAuth';

const API_BASE_URL = getApiBaseUrl();

const useAdminData = (user) => {
  const { accessToken } = useAuth();
  
  // State for all data
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [videoAccess, setVideoAccess] = useState([]);
  const [courseProgressData, setCourseProgressData] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  
  // Certificates pagination
  const [certificatesPagination, setCertificatesPagination] = useState({
    current_page: 1,
    per_page: 25,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pagination states
  const [usersPagination, setUsersPagination] = useState({
    current_page: 1,
    per_page: 25,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
    onPageChange: () => {}
  });

  const [videoAccessPagination, setVideoAccessPagination] = useState({
    current_page: 1,
    per_page: 25,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  const [courseProgressPagination, setCourseProgressPagination] = useState({
    current_page: 1,
    per_page: 25,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  // Search and filter states
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [groupSearchTerm, setGroupSearchTerm] = useState("");
  const [groupDomain, setGroupDomain] = useState("");
  const [certificateSearchTerm, setCertificateSearchTerm] = useState("");
  const [analyticsSearchTerm, setAnalyticsSearchTerm] = useState("");
  const [progressFilters, setProgressFilters] = useState({
    course_filter: "",
    user_filter: ""
  });

  // Fetch functions
  const fetchAdminCourses = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/courses?user_id=${user.id}`);
      const data = await response.json();
      setCourses(data.courses || data);
    } catch (error) {
      console.error("Error fetching admin courses:", error);
    }
  }, [user?.id]);

  const fetchUsers = useCallback(async (page = 1, search = "") => {
    if (!user?.id) {
      console.error("User ID not available");
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/admin/users?user_id=${user.id}&page=${page}&per_page=25&search=${encodeURIComponent(search)}`
      );
      const data = await response.json();
      setUsers(data.users || []);
      if (data.pagination) {
        setUsersPagination(data.pagination);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  }, [user?.id, API_BASE_URL]);

  const fetchCertificates = useCallback(async (page = 1, search = "") => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        user_id: user.id,
        page: page,
        per_page: 25
      });
      
      if (search) {
        params.append("search", search);
      }
      
      const response = await fetch(`${API_BASE_URL}/admin/certificates?${params}`);
      const data = await response.json();
      setCertificates(data.certificates || []);
      setCertificatesPagination(
        data.pagination || {
          current_page: page,
          per_page: 25,
          total: 0,
          total_pages: 0,
          has_next: false,
          has_prev: false
        }
      );
    } catch (error) {
      console.error("Error fetching certificates:", error);
      setError("Erro ao carregar certificados");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchVideoAccess = useCallback(async (page = 1, search = "") => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        user_id: user.id,
        page: page,
        per_page: 25
      });
      
      if (search) {
        params.append("search", search);
      }
      
      const response = await fetch(`${API_BASE_URL}/admin/analytics/video-access?${params}`);
      const data = await response.json();
      setVideoAccess(data.video_access || []);
      setVideoAccessPagination(
        data.pagination || {
          current_page: page,
          per_page: 25,
          total: 0,
          total_pages: 0,
          has_next: false,
          has_prev: false
        }
      );
    } catch (error) {
      console.error("Error fetching video access:", error);
      setError("Erro ao carregar relatório de acesso");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/groups?user_id=${user.id}`);
      const data = await response.json();
      setGroups(data.groups || []);
    } catch (error) {
      console.error("Error fetching groups:", error);
      setError("Erro ao carregar grupos");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchCourseProgress = useCallback(async (page = 1, courseFilter = "", userFilter = "") => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        user_id: user.id,
        page: page.toString(),
        per_page: "25"
      });
      
      if (courseFilter) params.append("course_filter", courseFilter);
      if (userFilter) params.append("user_filter", userFilter);
      
      const response = await fetch(`${API_BASE_URL}/admin/course-progress?${params}`);
      const data = await response.json();
      
      setCourseProgressData(data.course_progress || data.progress_data || []);
      setCourseProgressPagination(
        data.pagination || {
          current_page: page,
          per_page: 25,
          total: 0,
          total_pages: 0,
          has_next: false,
          has_prev: false
        }
      );
    } catch (error) {
      console.error("Error fetching course progress:", error);
      setError("Erro ao carregar progresso dos cursos");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchAvailableCourses = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/courses?user_id=${user.id}`);
      const data = await response.json();
      setAvailableCourses(data.courses || data);
    } catch (error) {
      console.error("Error fetching available courses:", error);
    }
  }, [user?.id]);

  // Initialize data on mount
  useEffect(() => {
    if (user?.id) {
      fetchAdminCourses();
      fetchUsers();
      fetchCertificates();
      fetchVideoAccess();
      fetchGroups();
      fetchCourseProgress();
      fetchAvailableCourses();
    }
  }, [user?.id, fetchAdminCourses, fetchUsers, fetchCertificates, fetchVideoAccess, fetchGroups, fetchCourseProgress, fetchAvailableCourses]);

  const returnValue = {
    // Data
    courses,
    users,
    certificates,
    groups,
    videoAccess,
    courseProgressData,
    availableCourses,
    
    // States
    loading,
    error,
    setError,
    
    // Pagination
    usersPagination,
    videoAccessPagination,
    courseProgressPagination,
    certificatesPagination,
    
    // Search and filters
    userSearchTerm,
    setUserSearchTerm,
    userSearchQuery,
    setUserSearchQuery,
    groupSearchTerm,
    setGroupSearchTerm,
    groupDomain,
    setGroupDomain,
    certificateSearchTerm,
    setCertificateSearchTerm,
    analyticsSearchTerm,
    setAnalyticsSearchTerm,
    progressFilters,
    setProgressFilters,
    
    // Fetch functions
    fetchUsers,
    fetchVideoAccess,
    fetchCourseProgress,
    fetchAdminCourses,
    fetchCertificates,
    fetchGroups,
    fetchAvailableCourses
  };
  
  return returnValue;
};

export default useAdminData;
